/**
 * Builds the Tool-stats drop list from one of two sources:
 *  - "clicks": drops that appear in the Binom click report for the date (the old behaviour).
 *  - "sent": every drop in the live SMSWiser account for the campaign(s) + date range
 *    (click-independent AND environment-independent), so 0-click drops show too.
 * Per-drop stats are fetched the same way (SMSWiser native) regardless of source.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';

import { useSmswiserCampaignDropsWithStats } from '@/hooks';
import api from '@/services/api';

import type { ToolDropRef } from '../ToolStatsMultiView';

export type DropSource = 'clicks' | 'sent';

interface UseToolDropListArgs {
  countryKey: string;
  wiserIds: number[];
  source: DropSource;
  /** ISO bounds for the click report and the live SMSWiser drops list. */
  rangeFrom: string;
  rangeTo: string;
  currency: string;
}

interface ApiDrop {
  uniqueId?: string;
  title?: string;
}

const ALNUM = /^[A-Za-z0-9]+$/;
const toLabel = (title: string | undefined, uid: string) => (title ? `${title} · ${uid}` : uid);

async function fetchCampaignDrops(
  countryKey: string,
  wiserId: number,
  from: string,
  to: string,
): Promise<ApiDrop[]> {
  const res = await api.get<{ data: { data?: ApiDrop[] } }>(
    `/smswiser/campaigns/${encodeURIComponent(countryKey)}/${wiserId}/drops`,
    { params: { from, to } },
  );
  // Enveloped response: { data: { recordsTotal, recordsFiltered, data: ApiDrop[] } }.
  const list = res.data?.data?.data;
  return Array.isArray(list) ? list : [];
}

/** Module-level (stable ref) so React Query memoises the merged list across renders. */
function combineSentDrops(
  results: ReadonlyArray<{ data?: ApiDrop[]; isLoading: boolean }>,
): { drops: ToolDropRef[]; isLoading: boolean } {
  const seen = new Set<string>();
  const drops: ToolDropRef[] = [];
  for (const r of results) {
    for (const d of Array.isArray(r.data) ? r.data : []) {
      const uid = d.uniqueId?.trim();
      if (!uid || !ALNUM.test(uid) || seen.has(uid)) continue;
      seen.add(uid);
      drops.push({ uniqueId: uid, label: toLabel(d.title?.trim(), uid) });
    }
  }
  return { drops, isLoading: results.some((r) => r.isLoading) };
}

export function useToolDropList(
  args: UseToolDropListArgs,
): { drops: ToolDropRef[]; isLoading: boolean } {
  const { countryKey, wiserIds, source, rangeFrom, rangeTo, currency } = args;

  const clicksQuery = useSmswiserCampaignDropsWithStats({
    countryKey,
    wiserIds,
    from: rangeFrom,
    to: rangeTo,
    currency,
    tz: 'Europe/Paris',
  });

  // "All sent": live SMSWiser drops for each campaign in the group, merged + deduped.
  const sent = useQueries({
    queries: wiserIds.map((wiserId) => ({
      queryKey: ['smswiser', 'campaign-drops', countryKey, wiserId, rangeFrom, rangeTo] as const,
      enabled: Boolean(countryKey) && wiserId > 0,
      queryFn: () => fetchCampaignDrops(countryKey, wiserId, rangeFrom, rangeTo),
      staleTime: 60_000,
    })),
    combine: combineSentDrops,
  });

  // Native tool stats need a real SMSWiser uniqueId. Drops-with-stats also emits synthetic rows
  // for Voluum traffic with no matching drop (id 0); those can't resolve tool stats, so drop them.
  const clicksDrops = useMemo<ToolDropRef[]>(() => {
    const seen = new Set<string>();
    const out: ToolDropRef[] = [];
    for (const r of clicksQuery.data?.rows ?? []) {
      const uid = r.drop.uniqueId?.trim();
      if (!uid || r.drop.id <= 0 || !ALNUM.test(uid) || seen.has(uid)) continue;
      seen.add(uid);
      out.push({ uniqueId: uid, label: toLabel(r.drop.title?.trim(), uid) });
    }
    return out;
  }, [clicksQuery.data?.rows]);

  return source === 'clicks'
    ? { drops: clicksDrops, isLoading: clicksQuery.isLoading }
    : { drops: sent.drops, isLoading: sent.isLoading };
}
